import React, { useState, useEffect, useContext, useRef } from "react";
import { Dialog, DialogTitle, DialogContent } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Alert from "@material-ui/lab/Alert";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useForm } from "react-hook-form";
import { makeStyles } from "@material-ui/core/styles";
import { useLocation, updateLocation, createLocation } from "../util/db";
import { useTheme } from '@material-ui/core/styles';
import Geocode from "react-geocode";
import MapForLocationSelectSearchAutocomplete from "./MapForLocationSelectSearchAutocomplete";
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { shootingTypes, prepwrapTypes, productionTypes } from "../util/locationTypes";
import CustomTypeDialogCreate from "./CustomTypeDialogCreate";
import CustomTypeDialogDelete from "./CustomTypeDialogDelete";
import { CategoryContext } from "../providers/CategoryProvider";
import { toast } from 'react-hot-toast';

const useStyles = makeStyles(() => ({
  paperDesktop: {
    // keep default modal feel
    display: "flex",
    flexDirection: "column",
    maxHeight: "calc(100vh - 64px)", // or whatever you had before
  },
  paperMobile: {
    display: "flex",
    flexDirection: "column",
    height: "100dvh",
    maxHeight: "100dvh",
    margin: 0,
    width: "100%",
    borderRadius: 0,
    overscrollBehavior: "contain",
  },
  content: {
    flex: 1,
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    overscrollBehavior: "contain",
  },
}));

function EditLocationModal(props) {
  const contentRef = useRef(null);
  const { category } = useContext(CategoryContext);
  const classes = useStyles();
  const [pending, setPending] = useState(false);
  const [formAlert, setFormAlert] = useState(null);
  const theme = useTheme();
  const ifSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const { register, handleSubmit, watch, formState: { isDirty } } = useForm();
  const { data: locationData, status: locationStatus } = useLocation(props.id);
  const [mapData, setMapData] = useState(null);
  const [typesList, setTypesList] = useState([]);
  const [type, setType] = useState(
    props.locationType || 
    (category === 'shooting' ? shootingTypes[0].type :
    category === 'prepwrap' ? prepwrapTypes[0].type :
    productionTypes[0].type)
  );
  const [createCustomTypeOpen, setCreateCustomTypeOpen] = useState(false);
  const [deleteCustomTypeOpen, setDeleteCustomTypeOpen] = useState(false);
  const typeOrderEntry = typesList.find((t) => t.type === type);
  const computedTypeOrder =
    typeOrderEntry?.orderNumber ??
    locationData?.typeOrder ??
    props.locationTypeOrder ??
    0;

  const values = watch(); // 🔥 live watch all form values

  Geocode.setApiKey(process.env.REACT_APP_GOOGLE_MAPS_API_KEY);

  useEffect(() => {
    setTypesList(
      category === 'shooting' && props.groupCustomTypes ? [...shootingTypes, ...props.groupCustomTypes] :
      category === 'shooting' ? [...shootingTypes] :
      category === 'prepwrap' && props.groupCustomTypes ? [...prepwrapTypes, ...props.groupCustomTypes] :
      category === 'prepwrap' ? [...prepwrapTypes] :
      category === 'production' && props.groupCustomTypes ? [...productionTypes, ...props.groupCustomTypes] :
      [...productionTypes]
    );
  }, [category, props.groupCustomTypes]);

  useEffect(() => {
  const body = document.body;
  const scrollY = window.scrollY;

  const prev = {
    overflow: body.style.overflow,
    position: body.style.position,
    top: body.style.top,
    width: body.style.width,
  };

  body.style.overflow = "hidden";
  body.style.position = "fixed";
  body.style.top = `-${scrollY}px`;
  body.style.width = "100%";

  return () => {
    body.style.overflow = prev.overflow;
    body.style.position = prev.position;
    body.style.top = prev.top;
    body.style.width = prev.width;
    window.scrollTo(0, scrollY);
  };
}, []);

useEffect(() => {
  if (props.id) {
    setMapData(locationData?.mapData);
    return;
  }

  // ✅ fallback immediately so UI never hangs blank
  setMapData({
    address: "Worcester, MA, USA",
    coords: { lat: 42.2625932, lng: -71.8022934 },
  });

  // ✅ hard timeout so Chrome can't hang forever
  let timedOut = false;
  const timeoutId = setTimeout(() => {
    timedOut = true;
    console.warn("Geolocation timed out; using fallback");
  }, 8000);

  console.log("isSecureContext", window.isSecureContext);

  navigator.geolocation.getCurrentPosition(
    (res) => {
      if (timedOut) return;
      clearTimeout(timeoutId);

      const lat = res.coords.latitude;
      const lng = res.coords.longitude;

      Geocode.fromLatLng(lat, lng)
        .then((response) => {
          const address =
            response.results?.[0]?.formatted_address ||
            "Unknown location";
          setMapData({ address, coords: { lat, lng } });
        })
        .catch((err) => console.error("Reverse geocode failed", err));
    },
    (err) => {
      clearTimeout(timeoutId);
      console.error("Geolocation error:", err); // <— this will tell you exact reason
      // keep fallback
    },
    { enableHighAccuracy: true, timeout: 7000, maximumAge: 60000 }
  );

  return () => clearTimeout(timeoutId);
}, [props.id, locationData?.mapData]);


  if (props.id && locationStatus !== "success") {
    return null;
  }

  const onSubmit = (data) => {
    setPending(true);
    const typeOrder = typesList.find((list) => list.type === type);
    const updatedData = {
      name: data?.name?.toUpperCase(),
      type,
      ...(props.new ? { typeOrder: computedTypeOrder } : {}),
      setName: ((/\bset\b/i.test(type))) ? data?.setName?.toUpperCase() : null,
      mapData: mapData,
      category: category,
      groupId: props.groupId
    };

    const query = props.id
      ? updateLocation(props.id, updatedData)
      : createLocation({ productionId: props.productionId, productionName: props.productionName, ...updatedData });

    query.then((response) => {
      if (response.success) {
        toast.success(response.message);
        props.onDone();
      } else {
        toast.error(response.message);
      }
    }).catch((error) => {
      toast.error(error.message);
      setPending(false);
    });
  };
  
  const ready = !!locationData || props.new; // locationData exists OR it's a new location

  // ---------- Validation helpers ----------
  const hasName = typeof values?.name === "string" && values.name.trim().length > 0;

  // setName required ONLY if type contains "set"
  const requiresSetName = /\bset\b/i.test(type);
  const hasSetName = !requiresSetName || (
    typeof values?.setName === "string" && values.setName.trim().length > 0
  );

  // mapData must exist and be complete
  const hasValidMap =
    mapData &&
    typeof mapData.address === "string" &&
    mapData.address.trim().length > 0 &&
    mapData.coords &&
    typeof mapData.coords.lat === "number" &&
    typeof mapData.coords.lng === "number";

  const hasCoords =
    mapData?.coords &&
    Number.isFinite(Number(mapData.coords.lat)) &&
    Number.isFinite(Number(mapData.coords.lng));

  // type must exist
  const hasType = typeof type === "string" && type.length > 0;

  const formIsValid =
    // hasName &&
    hasType &&
    hasSetName &&
    hasValidMap;

  // 🔥 Save Button Logic
  const nameChanged =
    values?.name?.toUpperCase() !== locationData?.name?.toUpperCase();

  const setNameChanged =
    requiresSetName &&
    values?.setName?.toUpperCase() !== locationData?.setName?.toUpperCase();

  const typeChanged = type !== locationData?.type;

  const mapChanged =
    JSON.stringify(mapData) !== JSON.stringify(locationData?.mapData);

  const hasChanges =
    nameChanged || setNameChanged || typeChanged || mapChanged;


  const hasOtherEmptyFields = Object.entries(values || {})
    .filter(([key]) => key !== 'name') // ignore name
    .some(([, value]) => value === '' || value === undefined);

const canSave = pending
  ? false
  : props.new
    ? formIsValid
    : ready && formIsValid && hasChanges;

  return (
    
<Dialog
  open
  onClose={props.onDone}
  fullScreen={ifSmall}
  fullWidth
  maxWidth="xl"
  scroll="paper"
  classes={{ paper: ifSmall ? classes.paperMobile : classes.paperDesktop }}
>
    <DialogTitle className="editHeader">
      {props.id ? "Update" : "Create"} Location
    </DialogTitle>

      <DialogContent ref={contentRef} className={classes.content}>
        {formAlert && (
          <Box mb={4}>
            <Alert severity={formAlert.type}>{formAlert.message}</Alert>
          </Box>
        )}

        {(locationStatus === "loading") ? (
          <Box py={5} px={3} align="center">
            <CircularProgress size={32} />
          </Box>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              {/* TYPE SELECT */}
              <Grid item xs={ifSmall ? 12 : ((/\bset\b/i.test(type)) ? 3 : 6)}>
                <TextField
                  variant="outlined"
                  id="locationTypeSelect"
                  label="TYPE"
                  name="type"
                  error={false}
                  select
                  fullWidth
                  value={type}
                  onChange={(e) => setType(e.target?.value || type)}
                >
                  {typesList?.sort((a, b) => a?.orderNumber - b?.orderNumber)
                    .map((theType) => (
                      <MenuItem key={theType?.orderNumber} value={theType?.type}>
                        {theType?.type} {props.groupCustomTypes?.some(ct => ct.type === theType?.type) && <span style={{ fontSize: '.7em', paddingLeft: 4 }}>(custom)</span>}
                      </MenuItem>
                    ))}
                  <div className={classes.buttonBar}>
                    {props.groupCustomTypes?.length > 0 && (
                      <Button variant="contained" color="secondary" onClick={() => setDeleteCustomTypeOpen(true)}>
                        DELETE CUSTOM
                      </Button>
                    )}
                    <Button variant="contained" color="primary" onClick={() => setCreateCustomTypeOpen(true)}>
                      + ADD CUSTOM
                    </Button>
                  </div>
                </TextField>
              </Grid>
              {/* SET NAME FIELD */}
              {(/\bset\b/i.test(type)) &&  (
                <Grid item xs={ifSmall ? 12 : ((/\bset\b/i.test(type)) ? 3 : 6)}>
                  <TextField
                    autoComplete="off"
                    variant="outlined"
                    type="text"
                    label="SET NAME"
                    name="setName"
                    defaultValue={locationData?.setName}
                    inputProps={{ style: { textTransform: "uppercase" } }}
                    inputRef={register({ required: "Please enter a set name" })}
                    fullWidth
                  />
                </Grid>
              )}

              {/* DESCRIPTION FIELD */}
              <Grid item xs={ifSmall ? 12 : 6}>
                <TextField
                  autoComplete="off"
                  variant="outlined"
                  type="text"
                  label="NAME / DESCRIPTION OF LOCATION (Optional)"
                  name="name"
                  inputProps={{ style: { textTransform: "uppercase" } }}
                  defaultValue={locationData?.name}
                  inputRef={register()}
                  fullWidth
                />
              </Grid>

              {/* MAP */}
              <Grid item xs={12}>
                {hasCoords ? (
                  <MapForLocationSelectSearchAutocomplete
                    setMapData={setMapData}
                    mapData={mapData}
                  />
                ) : (
                  <div style={{ padding: 16 }}>
                    Getting your location…
                  </div>
                )}
              </Grid>

              {/* FOOTER BUTTONS */}
              <Grid item xs={12} className="editFooter">
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  onClick={props.onDone}
                >
                  Cancel
                </Button>

                <Button
                  onClick={handleSubmit(onSubmit)}
                  variant="contained"
                  color="primary"
                  size="large"
                  type="button"
                  disabled={pending || !canSave}
                >
                  {!pending ? <span>Save</span> : <CircularProgress size={28} />}
                </Button>
              </Grid>
            </Grid>
          </form>
        )}

        {/* Custom Type Dialogs */}
        <CustomTypeDialogCreate open={createCustomTypeOpen} setOpen={setCreateCustomTypeOpen} fullTypesList={typesList} customTypes={props.groupCustomTypes} groupId={props.groupId} listLength={typesList?.length} setType={setType} />
        <CustomTypeDialogDelete open={deleteCustomTypeOpen} setOpen={setDeleteCustomTypeOpen} groupId={props.groupId} customTypes={props.groupCustomTypes} />
      </DialogContent>
    </Dialog>
  );
}

export default EditLocationModal;
